import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Users2, Search, Crown, Loader2, Eye, Pencil, UserPlus, Shield, Lock, Unlock, ShieldCheck, ShieldAlert } from "lucide-react";
import { Card } from "@/components/common";
import { TEAM_ROLE_CHIP, SCOPE_LABEL, ROLE_LABEL, deDT, teamRoleLabel, downloadCsv, ExportBtn, Pagination, Drawer } from "./shared";
import { Avatar, avatarRing, TwoFACell, accountStatus, DrawerSection, KV } from "./ui";

function ActionPill({ icon: Icon, label, onClick, disabled, tone, testid, title }) {
  const cls = disabled
    ? "border-white/5 text-gray-600 cursor-not-allowed opacity-40"
    : tone === "red"
      ? "border-red-500/25 text-red-300 hover:bg-red-500/10 hover:border-red-500/50"
      : "border-white/10 text-gray-300 hover:bg-white/10 hover:text-white hover:border-white/20";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title || label} aria-label={label}
      className={`inline-flex items-center justify-center gap-1.5 min-w-[36px] min-h-[36px] px-2 min-[1900px]:px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${cls}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" /> <span className="hidden min-[1900px]:inline">{label}</span>
    </button>
  );
}

export function TeamTab({ ctx }) {
  const { t, teamUsers, users, clients, matrix, busy, actions, nav } = ctx;
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detail, setDetail] = useState(null);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return teamUsers.filter((u) => !s
      || (u.name || "").toLowerCase().includes(s)
      || (u.email || "").toLowerCase().includes(s)
      || teamRoleLabel(matrix, u.team_role).toLowerCase().includes(s));
  }, [teamUsers, q, matrix]);

  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const cur = Math.min(page, pages);
  const rows = list.slice((cur - 1) * perPage, cur * perPage);
  const cmap = useMemo(() => Object.fromEntries((clients || []).map((c) => [c.id, c.company_name])), [clients]);

  const scopeText = (u) => (u.role === "super_admin" || u.client_scope === "alle") ? t("Alle Kunden")
    : u.client_scope === "bestimmte" ? `${(u.scoped_client_ids || []).length} ${t("Kunden")}` : t("Keine Kunden");

  const exportCsv = () => downloadCsv("kewag-team.csv", [
    { label: "Name", value: "name" }, { label: "E-Mail", value: "email" },
    { label: "Team-Rolle", value: (u) => teamRoleLabel(matrix, u.team_role) },
    { label: "Kunden-Zuweisung", value: (u) => scopeText(u) },
    { label: "2FA", value: (u) => (u.totp_enabled ? "Aktiv" : "Offen") },
    { label: "Letzter Login", value: (u) => deDT(u.last_login) || "Noch nie" },
    { label: "Konto-Status", value: (u) => accountStatus(u, (x) => x).label },
  ], list);

  return (
    <>
      <Card className="p-0 overflow-hidden" data-testid="sec-panel-team">
        <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-bold tracking-tight text-sm flex items-center gap-2"><Users2 className="w-4 h-4 text-[#A3E635]" /> {t("Internes KEWAG-Team")} <span className="text-gray-500 font-normal">{teamUsers.length}</span></div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="team-search" placeholder={t("Suchen nach Name, E-Mail oder Rolle…")} className="bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs w-64 focus:border-[#A3E635] outline-none" />
            </div>
            <ExportBtn onClick={exportCsv} label={t("Exportieren")} disabled={list.length === 0} title={t("Als CSV exportieren")} testid="team-export" />
          </div>
        </div>
        <div className="hidden min-[1900px]:block overflow-x-auto" data-testid="team-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                <th className="text-left font-semibold px-4 py-3">{t("Name")}</th>
                <th className="text-left font-semibold px-3 py-3">{t("Team-Rolle")}</th>
                <th className="text-left font-semibold px-3 py-3">{t("Kunden-Zuweisung")}</th>
                <th className="text-left font-semibold px-3 py-3">2FA</th>
                <th className="text-left font-semibold px-3 py-3 hidden md:table-cell">{t("Letzter Login")}</th>
                <th className="text-left font-semibold px-3 py-3">{t("Konto-Status")}</th>
                <th className="text-right font-semibold px-3 py-3">{t("Aktionen")}</th>
              </tr>
            </thead>
            <tbody>
              {!users && <tr><td colSpan={7} className="px-5 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></td></tr>}
              {users && total === 0 && <tr><td colSpan={7} className="px-5 py-8 text-gray-500 text-sm">{t("Keine Benutzer gefunden.")}</td></tr>}
              {rows.map((u) => {
                const isOwner = u.role === "super_admin";
                const s = accountStatus(u, t);
                const locked = u.active === false || u.locked;
                const working = busy === u.id;
                return (
                  <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]" data-testid={`team-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={u.name} accent={avatarRing(u)} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate max-w-[200px] flex items-center gap-1.5">{isOwner && <Crown className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />}{u.name}</div>
                          <div className="text-[11px] text-gray-500 truncate max-w-[200px]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${TEAM_ROLE_CHIP[u.team_role] || "bg-white/5 border-white/10 text-gray-400"}`} data-testid={`team-role-${u.id}`}>{teamRoleLabel(matrix, u.team_role)}</span></td>
                    <td className="px-3 py-3 text-xs text-gray-300 whitespace-nowrap" data-testid={`team-scope-${u.id}`}>{scopeText(u)}</td>
                    <td className="px-3 py-3" data-testid={`team-2fa-${u.id}`}><TwoFACell on={u.totp_enabled} t={t} /></td>
                    <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap hidden md:table-cell">{deDT(u.last_login) || t("Noch nie")}</td>
                    <td className="px-3 py-3" data-testid={`team-status-${u.id}`}><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${s.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {working
                          ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          : <>
                            <ActionPill icon={Eye} label={t("Anzeigen")} onClick={() => setDetail(u)} testid={`team-view-${u.id}`} />
                            <ActionPill icon={Pencil} label={t("Bearbeiten")} onClick={() => actions.edit(u)} testid={`team-edit-${u.id}`} />
                            <ActionPill icon={UserPlus} label={t("Zuweisung")} onClick={() => actions.scope(u)} disabled={isOwner} title={isOwner ? t("Owner — geschützt") : t("Kunden zuweisen")} testid={`team-assign-${u.id}`} />
                            <ActionPill icon={Shield} label={t("Rechte")} onClick={() => actions.teamRole(u)} disabled={isOwner} title={isOwner ? t("Owner — geschützt") : t("Team-Rolle ändern")} testid={`team-rights-${u.id}`} />
                            <ActionPill icon={Lock} label="2FA" onClick={() => actions.reset2fa(u)} title={t("2FA zurücksetzen")} testid={`team-2fa-btn-${u.id}`} />
                            {locked
                              ? <ActionPill icon={Unlock} label={u.prepared ? t("Freischalten") : t("Entsperren")} onClick={() => actions.unlock(u)} disabled={isOwner} testid={`team-unlock-${u.id}`} />
                              : <ActionPill icon={Lock} label={t("Sperren")} onClick={() => actions.lock(u)} disabled={isOwner} tone="red" title={isOwner ? t("Owner — geschützt") : t("Konto sperren")} testid={`team-lock-${u.id}`} />}
                          </>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="min-[1900px]:hidden grid md:grid-cols-2" data-testid="team-cards">
          {users && total === 0 && <div className="px-5 py-8 text-gray-500 text-sm">{t("Keine Benutzer gefunden.")}</div>}
          {rows.map((u) => <TeamCard key={u.id} u={u} t={t} matrix={matrix} scopeText={scopeText} busy={busy} actions={actions} onView={() => setDetail(u)} />)}
        </div>
        {total > 0 && <Pagination page={cur} setPage={setPage} total={total} perPage={perPage} setPerPage={setPerPage} t={t} testid="team-pagination" />}
      </Card>

      {detail && <TeamDetailDrawer u={detail} t={t} matrix={matrix} cmap={cmap} onClose={() => setDetail(null)} actions={actions} nav={nav} />}
    </>
  );
}

function TeamDetailDrawer({ u, t, matrix, cmap, onClose, actions, nav }) {
  const isOwner = u.role === "super_admin";
  const s = accountStatus(u, t);
  const locked = u.active === false || u.locked;
  const scope = u.role === "super_admin" ? "alle" : (["alle", "bestimmte", "keine"].includes(u.client_scope) ? u.client_scope : "keine");
  const ids = u.scoped_client_ids || [];
  return (
    <Drawer title={t("Benutzerdetails")} onClose={onClose} testid="team-detail-drawer">
      <div className="mb-5 flex items-center gap-3">
        <Avatar name={u.name} accent={avatarRing(u)} size="lg" />
        <div className="min-w-0">
          <div className="font-bold text-base flex items-center gap-1.5">{isOwner && <Crown className="w-4 h-4 text-[#A3E635]" />}{u.name}</div>
          <div className="text-xs text-gray-500 truncate">{u.email}</div>
          <span className={`mt-1 inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${TEAM_ROLE_CHIP[u.team_role] || "bg-white/5 border-white/10 text-gray-400"}`}>{teamRoleLabel(matrix, u.team_role)}</span>
        </div>
      </div>

      <DrawerSection title={t("Übersicht")}>
        <KV label={t("Team-Rolle")} value={teamRoleLabel(matrix, u.team_role)} />
        <KV label={t("Konto-Status")} value={<span className={`inline-flex items-center gap-1.5 ${s.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span>} />
        <KV label="2FA" value={u.totp_enabled ? t("Aktiv") : t("Offen")} accent={u.totp_enabled ? "text-[#A3E635]" : "text-amber-400"} />
        <KV label={t("Letzter Login")} value={deDT(u.last_login) || t("Noch nie")} />
        <KV label={t("Erstellt am")} value={deDT(u.created_at) || "—"} />
        <KV label={t("Passwort läuft ab")} value={deDT(u.password_expires_at) || t("Nicht festgelegt")} />
      </DrawerSection>

      <DrawerSection title={t("Serverseitig wirksame Rechte")}>
        <KV label={t("Konto-ID")} value={<span className="font-mono text-[11px] break-all" data-testid="team-detail-account-id">{u.id}</span>} />
        <KV label={t("Erlaubte Bereiche")} value={<span data-testid="team-detail-effective-sections">{(u.effective_sections || []).length ? u.effective_sections.map((k) => t((matrix?.sections || []).find((x) => x.key === k)?.label || k)).join(", ") : t("Keine")}</span>} />
        {u.scope_invalid && <p className="text-xs text-red-300 mt-1" data-testid="team-detail-scope-invalid">{t("Kunden-Zuordnung ungültig gespeichert: gewährt keinen Kundenzugriff. Bitte bewusst neu zuweisen.")}</p>}
      </DrawerSection>
      <DrawerSection title={t("Kunden-Zuweisung")}>
        <KV label={t("Scope")} value={t(SCOPE_LABEL[scope] || "Keine Kunden")} />
        {scope === "bestimmte" && (
          <div className="py-2">
            {ids.length === 0 ? <span className="text-[11px] text-amber-400">{t("Keine Kunden ausgewählt")}</span>
              : <div className="flex flex-wrap gap-1.5">{ids.map((cid) => <span key={cid} className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-gray-300">{cmap[cid] || cid}</span>)}</div>}
          </div>
        )}
      </DrawerSection>

      <TeamActivity uid={u.id} t={t} />

      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { onClose(); actions.edit(u); }} data-testid="detail-edit" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><Pencil className="w-4 h-4 text-[#A3E635]" /> {t("Bearbeiten")}</button>
        <button onClick={() => { onClose(); actions.reset2fa(u); }} data-testid="detail-2fa" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><Lock className="w-4 h-4 text-[#A3E635]" /> 2FA</button>
        {!isOwner && <button onClick={() => { onClose(); actions.scope(u); }} data-testid="detail-assign" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><UserPlus className="w-4 h-4 text-[#A3E635]" /> {t("Zuweisung")}</button>}
        {!isOwner && <button onClick={() => { onClose(); actions.teamRole(u); }} data-testid="detail-rights" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><Shield className="w-4 h-4 text-[#A3E635]" /> {t("Rechte")}</button>}
        {isOwner && <div className="col-span-2 flex items-center justify-center gap-1.5 text-[11px] text-[#A3E635] font-semibold py-2"><Crown className="w-3.5 h-3.5" /> {t("Owner — geschützt")}</div>}
      </div>
    </Drawer>
  );
}

function TeamCard({ u, t, matrix, scopeText, busy, actions, onView }) {
  const isOwner = u.role === "super_admin";
  const s = accountStatus(u, t);
  const locked = u.active === false || u.locked;
  return (
    <div className="px-4 py-4 border-b border-white/5 md:odd:border-r min-w-0" data-testid={`team-card-${u.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={u.name} accent={avatarRing(u)} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate flex items-center gap-1.5">{isOwner && <Crown className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />}<span className="truncate">{u.name}</span></div>
          <div className="text-[11px] text-gray-500 truncate">{u.email}</div>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-xs">
        <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">{t("Team-Rolle")}</dt><dd className="mt-0.5 text-gray-200" data-testid={`team-card-role-${u.id}`}>{teamRoleLabel(matrix, u.team_role)}</dd></div>
        <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">{t("Kunden-Zuweisung")}</dt><dd className="mt-0.5 text-gray-200" data-testid={`team-card-scope-${u.id}`}>{scopeText(u)}</dd></div>
        <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">{t("Konto-Status")}</dt><dd className={`mt-0.5 inline-flex items-center gap-1.5 font-semibold ${s.txt}`} data-testid={`team-card-status-${u.id}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</dd></div>
        <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">2FA</dt><dd className="mt-0.5"><TwoFACell on={u.totp_enabled} t={t} /></dd></div>
        <div className="col-span-2"><dt className="text-[10px] uppercase tracking-wider text-gray-500">{t("Letzter Login")}</dt><dd className="mt-0.5 text-gray-300" data-testid={`team-card-login-${u.id}`}>{deDT(u.last_login) || t("Noch nie")}</dd></div>
      </dl>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {busy === u.id ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : <>
          <ActionPill icon={Eye} label={t("Anzeigen")} onClick={onView} testid={`team-card-view-${u.id}`} />
          <ActionPill icon={Pencil} label={t("Bearbeiten")} onClick={() => actions.edit(u)} testid={`team-card-edit-${u.id}`} />
          <ActionPill icon={UserPlus} label={t("Zuweisung")} onClick={() => actions.scope(u)} disabled={isOwner} testid={`team-card-assign-${u.id}`} />
          <ActionPill icon={Shield} label={t("Rechte")} onClick={() => actions.teamRole(u)} disabled={isOwner} testid={`team-card-rights-${u.id}`} />
          <ActionPill icon={Lock} label="2FA" onClick={() => actions.reset2fa(u)} testid={`team-card-2fa-${u.id}`} />
          {locked
            ? <ActionPill icon={Unlock} label={u.prepared ? t("Freischalten") : t("Entsperren")} onClick={() => actions.unlock(u)} disabled={isOwner} testid={`team-card-unlock-${u.id}`} />
            : <ActionPill icon={Lock} label={t("Sperren")} onClick={() => actions.lock(u)} disabled={isOwner} tone="red" testid={`team-card-lock-${u.id}`} />}
        </>}
      </div>
    </div>
  );
}

function TeamActivity({ uid, t }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/users/${uid}/activity`).then((r) => alive && setData(r.data)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [uid]);
  return (
    <DrawerSection title={t("Protokollierte Änderungen")}>
      <div data-testid="team-activity">
        {err && <div className="text-[11px] text-red-400 py-2" data-testid="team-activity-error">{t("Aktion fehlgeschlagen")}</div>}
        {!data && !err && <Loader2 className="w-4 h-4 animate-spin text-gray-500 my-2" />}
        {data && data.activity.length === 0 && <div className="text-[11px] text-gray-500 py-2" data-testid="team-activity-empty">{t("Noch keine protokollierten Änderungen.")}</div>}
        {data && data.activity.map((a, i) => (
          <div key={i} className="py-1.5 border-b border-white/5 last:border-0 text-xs" data-testid={`team-activity-${i}`}>
            <div className="flex justify-between gap-3"><span className="text-gray-200 font-medium truncate">{a.action}</span><span className="text-gray-500 whitespace-nowrap">{deDT(a.created_at)}</span></div>
            {(a.company_name || a.fields.length > 0) && <div className="text-[11px] text-gray-500 truncate">{[a.company_name, a.fields.join(", ")].filter(Boolean).join(" · ")}</div>}
          </div>
        ))}
        {data && data.security.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Anmeldeereignisse")}</div>
            {data.security.slice(0, 5).map((e, i) => (
              <div key={i} className="flex justify-between gap-3 text-[11px] py-0.5" data-testid={`team-security-${i}`}>
                <span className={e.success ? "text-gray-300" : "text-amber-400"}>{e.event}</span><span className="text-gray-500 whitespace-nowrap">{deDT(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DrawerSection>
  );
}
